import { Boundaries } from "@planzone/core";
import { id, role } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { project, account }: Boundaries["retrieveProjectRole"]["request"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToPending(),
          role: { uuid: id, ...role },
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resource: account.resource,
          request: setRequestStateToPending(),
          role: { uuid: id, ...role },
        },
      ];
}

function handleSuccess(
  state: State,
  { project, role, account }: Boundaries["retrieveProjectRole"]["success"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToSuccess(),
          role,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resource: account.resource,
          request: setRequestStateToSuccess(),
          role,
        },
      ];
}

function handleFailure(
  state: State,
  { project, account }: Boundaries["retrieveProjectRole"]["failure"],
): State {
  const index = state.findIndex((req) => req.project.uuid === project.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          project,
          request: setRequestStateToFailure(),
          role: { uuid: id, ...role },
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          project,
          resource: account.resource,
          request: setRequestStateToFailure(),
          role: { uuid: id, ...role },
        },
      ];
}

function handleEditProjectResource(
  state: State,
  projectResource: Boundaries["editProjectResource"]["success"],
): State {
  const index = state.findIndex(
    (req) =>
      req.project.uuid === projectResource.project.uuid &&
      req.resource.uuid === projectResource.resource.uuid,
  );

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], role: projectResource.role },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleRetrieveProjectsRoles(
  state: State,
  { projectResources, account }: Boundaries["retrieveProjectsRoles"]["success"],
): State {
  return [
    ...state,
    ...projectResources.map(({ role, project }) => ({
      project,
      resource: account.resource,
      request: setRequestStateToSuccess(),
      role,
    })),
  ];
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrieveProjectRoleRequested]: handleRequest,
    [events.retrieveProjectRoleSucceeded]: handleSuccess,
    [events.retrieveProjectRoleFailed]: handleFailure,
    [events.editProjectResourceSucceeded]: handleEditProjectResource,
    [events.retrieveProjectsRolesSucceeded]: handleRetrieveProjectsRoles,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
